import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Box, ListItem, ListItemButton, ListItemText, Typography } from "@mui/material";
import { StyledCheckbox } from "@shared/form/CheckboxField";

import { DeviceFormKey, toDeviceKey, treeItemIndentPx } from "../CreateRolloutSidePanel.utils";

import { useStyles } from "./RolloutTreeRows.styles";
import {
    DeviceTreeNode,
    getAllDeviceIdsInGroup,
    getGroupSelectionState,
    GroupSelectionState,
    GroupTreeNode,
} from "./RolloutUnifiedTree.utils";

const itemHeight = 48;

export const rowEstimateSize = itemHeight;

type GroupRowProps = {
    group: GroupTreeNode;
    depth: number;
    expandedGroups: Set<number>;
    selectedDeviceIds: Set<number>;
    onToggleExpand: (groupId: number) => void;
    onChange: (id: DeviceFormKey, value: boolean) => void;
    onBatchChange?: (updates: Array<{ id: DeviceFormKey; value: boolean }>) => void;
};

export const GroupRow = React.memo<GroupRowProps>(
    ({ group, depth, expandedGroups, selectedDeviceIds, onToggleExpand, onChange, onBatchChange }) => {
        const { classes } = useStyles();
        const { t } = useTranslation(["sidepanels"]);

        const isExpanded = expandedGroups.has(group.id);
        const hasExpandableContent = group.children.length > 0 || group.devices.length > 0;

        const selectionState: GroupSelectionState = useMemo(
            () => getGroupSelectionState(group, selectedDeviceIds),
            [group, selectedDeviceIds],
        );

        const isChecked = selectionState === "all";
        const isIndeterminate = selectionState === "partial";

        const onGroupSelectToggle = () => {
            const shouldSelect = selectionState !== "all";
            const allDeviceIds = getAllDeviceIdsInGroup(group);
            if (onBatchChange && allDeviceIds.length > 1) {
                onBatchChange(allDeviceIds.map((deviceId) => ({ id: toDeviceKey(deviceId), value: shouldSelect })));
            } else {
                allDeviceIds.forEach((deviceId) => {
                    onChange(toDeviceKey(deviceId), shouldSelect);
                });
            }
        };

        const onRowClick = () => {
            if (hasExpandableContent) {
                onToggleExpand(group.id);
            }
        };

        const expandAriaLabel = t(
            isExpanded ? "sidepanels:rolloutPanel.aria.collapseGroup" : "sidepanels:rolloutPanel.aria.expandGroup",
            { name: group.name },
        );

        return (
            <ListItem style={{ paddingLeft: `${depth * treeItemIndentPx}px` }} disablePadding className={classes.rowHeight}>
                <ListItemButton
                    onClick={onRowClick}
                    className={classes.listItemButton}
                    aria-expanded={hasExpandableContent ? isExpanded : undefined}
                    aria-label={hasExpandableContent ? expandAriaLabel : undefined}
                >
                    <Box className={classes.itemContent}>
                        <Box
                            component="span"
                            className={classes.chevron}
                            style={{ visibility: hasExpandableContent ? "visible" : "hidden" }}
                            aria-hidden="true"
                        >
                            <Typography variant="caption" component="span">
                                <i className={isExpanded ? "fa-solid fa-chevron-down" : "fa-solid fa-chevron-right"} />
                            </Typography>
                        </Box>

                        <StyledCheckbox
                            checked={isChecked}
                            indeterminate={isIndeterminate}
                            onClick={(e) => e.stopPropagation()}
                            onChange={onGroupSelectToggle}
                            slotProps={{
                                input: { "aria-label": t("sidepanels:rolloutPanel.aria.groupCheckbox", { name: group.name }) },
                            }}
                        />

                        <ListItemText
                            className={classes.listItemText}
                            primary={
                                <Box component="span" display="flex" alignItems="center">
                                    <Typography variant="body2">{group.name}</Typography>
                                    <Typography variant="caption" className={classes.deviceCount}>
                                        {t("sidepanels:rolloutPanel.groupDeviceCount", {
                                            count: group.totalDeviceCount,
                                        })}
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>
                </ListItemButton>
            </ListItem>
        );
    },
);

type DeviceRowProps = {
    device: DeviceTreeNode;
    depth: number;
    isSelected: boolean;
    isHighlighted: boolean;
    onChange: (id: DeviceFormKey, value: boolean) => void;
};

export const DeviceRow = React.memo<DeviceRowProps>(({ device, depth, isSelected, isHighlighted, onChange }) => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation(["sidepanels"]);

    const onDeviceClick = () => {
        onChange(toDeviceKey(device.id), !isSelected);
    };

    return (
        <ListItem
            style={{ paddingLeft: `${depth * treeItemIndentPx}px` }}
            disablePadding
            className={cx(classes.rowHeight, { [classes.highlightedDevice]: isHighlighted })}
        >
            <ListItemButton onClick={onDeviceClick} className={classes.listItemButton}>
                <Box className={classes.itemContent}>
                    <Box style={{ width: 32 }} />

                    <StyledCheckbox
                        checked={isSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={onDeviceClick}
                        slotProps={{
                            input: { "aria-label": t("sidepanels:rolloutPanel.aria.deviceCheckbox", { name: device.name }) },
                        }}
                    />

                    <ListItemText
                        className={classes.listItemText}
                        primary={
                            <Box component="span" display="flex" alignItems="center">
                                <Typography variant="body2" className={classes.deviceName}>
                                    {device.name}
                                </Typography>
                                {device.serial && (
                                    <Typography variant="caption" className={classes.deviceSerial}>
                                        ({device.serial})
                                    </Typography>
                                )}
                            </Box>
                        }
                    />
                </Box>
            </ListItemButton>
        </ListItem>
    );
});
